import React, { useState } from 'react';
import { Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';

const faqItems = [
  {
    id: 1,
    question: 'WHY CHOOSE COACHING?',
    answer: 'Coaching is about the future. It is about creating a future with a sense of creativity and commitment. Choosing coaching can be a transformative investment in your personal and professional life. A coach provides valuable guidance, support, and accountability, helping you gain clarity, set goals, and develop an action plan. Coaches assist in overcoming obstacles, building resilience, and making better decisions. Coaching encourages self-discovery, fostering personal growth, and empowering you to unlock your potential. It enhances your well-being, work-life balance, and accelerates your progress toward achieving meaningful goals. With coaching, you can tap into your strengths, navigate challenges, and create positive change, making it a powerful choice for personal development.'
  },
  {
    id: 2,
    question: 'HOW DOES IT WORK?',
    answer: 'Coaching typically involves a collaborative partnership between a coach and a client. The process begins with the coach and the client getting into a "Chemistry" session. It is important that the coach and the client establish understanding and trust. A formal contract document is agreed upon, and thereafter, the coach understands the client\'s goals, values, and challenges. Through one-on-one sessions, the coach employs active listening, powerful questioning, various coaching techniques to facilitate self-reflection and explore possibilities. Regular sessions maintain momentum and evaluate progress, ultimately empowering the client to achieve their desired outcomes in a structured and supportive coaching relationship.'
  },
  {
    id: 3,
    question: 'HOW IS IT DIFFERENT FROM THERAPY?',
    answer: 'Coaching and therapy are distinct disciplines that serve different purposes. Coaching focuses on the present and future, aiming to help individuals clarify goals and achieve personal and professional growth. It is action-oriented, goal-driven, and emphasises accountability. Therapy, on the other hand, typically delves into the past to explore emotional healing, address psychological issues, and improve mental well-being. Therapy often involves diagnosing and treating mental health conditions such as trauma and depression. While coaching may touch on behaviour, emotions and mindset, its primary focus is on empowering individuals to create change and achieve their goals, whereas therapy focuses on resolving psychological issues and promoting overall mental health and returning to healthy baseline.'
  },
  {
    id: 4,
    question: 'HOW WILL IT HELP?',
    answer: 'Coaching can greatly assist you in achieving your goals. Through a partnership with your coach, you can explore self limiting beliefs and unknown issues that block your progress. A coach can help you stay focused, motivated, and on track, increasing your chances of successfully achieving your desired outcomes.'
  }
];

function FAQSection() {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box 
      id="faq"
      sx={{ 
        py: { xs: 6, md: 8 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <Box textAlign="center" mb={6}>
          <Typography 
            variant="h3" 
            component="h2"
            sx={{ 
              color: 'primary.main',
              mb: 2,
              fontWeight: 700
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{
              maxWidth: '700px',
              mx: 'auto',
              mb: 4
            }}
          >
            Find answers to common questions about coaching and how it can benefit you
          </Typography>
        </Box>

        <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
          {faqItems.map((item) => (
            <Accordion 
              key={item.id} 
              expanded={expanded === `panel${item.id}`} 
              onChange={handleChange(`panel${item.id}`)}
              elevation={2}
              sx={{
                mb: 2,
                borderRadius: '8px !important',
                overflow: 'hidden',
                '&:before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${item.id}bh-content`}
                id={`panel${item.id}bh-header`}
                sx={{
                  backgroundColor: expanded === `panel${item.id}` ? 'primary.light' : 'background.paper',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&.Mui-expanded': {
                    minHeight: '48px',
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                    },
                  },
                }}
              >
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: 'background.paper',
                  p: 3,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default FAQSection;
